import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Search, Trash2 } from "lucide-react";
import { useCountry } from "../hooks/useCountries";
import { useStatesByCountry, useStateManagement } from "../hooks/useState";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";

import {
  State,
  CreateStateRequest,
  UpdateStateRequest,
} from "../api/services/state/type";
import toast from "react-hot-toast";
import { StateTable } from "../components/countries/states/StateTable";
import { StateForm } from "../components/countries/states/StateForm";
import { Breadcrumb } from "../components/common/Breadcrumb";

export const CountryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingState, setEditingState] = useState<State | undefined>(
    undefined,
  );

  const {
    data: country,
    isLoading: isCountryLoading,
    isError: isCountryError,
  } = useCountry(id || "");

  const {
    data: states = [],
    isLoading: isStatesLoading,
    refetch: refetchStates,
  } = useStatesByCountry(id || "");

  const { createState, updateState, deleteState, isCreating, isUpdating } =
    useStateManagement();

  const filteredStates = states.filter((state) => {
    if (!searchTerm) return true;
    return state.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateState = () => {
    setEditingState(undefined);
    setShowForm(true);
  };

  const handleEditState = (state: State) => {
    setEditingState(state);
    setShowForm(true);
  };

  const handleFormSubmit = (data: CreateStateRequest | UpdateStateRequest) => {
    if (editingState) {
      updateState(
        { stateId: editingState.id, data: data as UpdateStateRequest },
        {
          onSuccess: () => {
            toast.success("State updated successfully!");
            setShowForm(false);
            setEditingState(undefined);
            refetchStates();
          },
          onError: (error) => {
            toast.error(`Failed to update state: ${error.message}`);
          },
        },
      );
    } else {
      const createData = {
        ...data,
        countryId: id!,
      } as CreateStateRequest;

      createState(createData, {
        onSuccess: () => {
          toast.success("State created successfully!");
          setShowForm(false);
          refetchStates();
        },
        onError: (error) => {
          toast.error(`Failed to create state: ${error.message}`);
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stateToDelete, setStateToDelete] = useState<string | null>(null);

  const handleDeleteState = (stateId: string) => {
    setStateToDelete(stateId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (stateToDelete) {
      deleteState(stateToDelete, {
        onSuccess: () => {
          toast.success("State deleted successfully!");
          setDeleteDialogOpen(false);
          setStateToDelete(null);
          refetchStates();
        },
        onError: (error) => {
          toast.error(`Failed to delete state: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setStateToDelete(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingState(undefined);
  };

  const breadcrumbItems = [
    { label: "Countries", href: "/countries" },
    { label: country?.name || "Loading..." },
  ];

  if (isCountryLoading) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-slate-400">Loading country details...</span>
      </div>
    );
  }

  if (isCountryError || !country) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: "Countries", href: "/countries" },
              { label: "Error" },
            ]}
          />
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Country
            </h3>
            <p className="text-red-300">
              Failed to load country details. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* States Section */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search states by name..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleCreateState}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add State
            </button>
          </div>

          {/* States Table */}
          <StateTable
            states={filteredStates}
            onEdit={handleEditState}
            onDelete={handleDeleteState}
            isLoading={isStatesLoading}
          />

          {filteredStates.length === 0 && !isStatesLoading && (
            <div className="text-center py-12">
              <div className="text-slate-400">
                {searchTerm
                  ? "No states found matching your search."
                  : "No states found for this country."}
              </div>
              {!searchTerm && (
                <button
                  onClick={handleCreateState}
                  className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  <Plus size={16} />
                  Add First State
                </button>
              )}
            </div>
          )}
        </div>

        {/* State Form Modal */}
        {showForm && (
          <StateForm
            state={editingState}
            countryName={country.name}
            isLoading={isCreating || isUpdating}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete State"
          description="Are you sure you want to delete this state? This action cannot be undone and will fail if customers are associated with this state."
          icon={<Trash2 className="h-8 w-8 text-white" />}
          iconBgColor="bg-red-600"
          confirmColor="bg-red-600 hover:bg-red-700"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};
